import { useState } from 'react';
import { Flag } from 'lucide-react';
import { Modal } from './Modal';
import { dbInsert, getStoredSession } from '../lib/api';

interface ReportProblemButtonProps {
  projectId: string;
  projectName: string;
}

export function ReportProblemButton({ projectId, projectName }: ReportProblemButtonProps) {
  const [showModal, setShowModal] = useState(false);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const session = getStoredSession();

    const { error: insertError } = await dbInsert('problem_reports', {
      project_id: projectId,
      user_id: session?.userId || null,
      description: description.trim() || null,
      page_url: window.location.href,
    }, {
      token: session?.accessToken,
      returning: false,
    });

    setSubmitting(false);

    if (insertError) {
      setError('Failed to submit report. Please try again.');
      return;
    }

    setSubmitted(true);
    setDescription('');
  };

  const handleClose = () => {
    setShowModal(false);
    setSubmitted(false);
    setError(null);
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="flex items-center gap-1.5 text-[10px] sm:text-xs px-2 py-1 rounded-lg bg-theme-surface/80 backdrop-blur-sm border border-theme-border-muted text-theme-text-muted hover:text-theme-text hover:bg-theme-surface transition-colors"
        title="Report a problem with this mini-app"
      >
        <Flag className="h-3 w-3" />
        <span className="hidden sm:inline">Report a problem</span>
      </button>

      <Modal
        isOpen={showModal}
        onClose={handleClose}
        title={`Report a problem`}
        size="sm"
      >
        {submitted ? (
          <div className="text-center py-4">
            <p className="text-theme-text font-medium mb-2">Thank you!</p>
            <p className="text-sm text-theme-text-muted mb-4">
              Your report for "{projectName}" has been submitted.
            </p>
            <button onClick={handleClose} className="btn btn-secondary">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-theme-text-muted">
              Report a problem with "{projectName}".
            </p>
            <div>
              <label htmlFor="reportDescription" className="block text-sm font-medium text-theme-text-secondary mb-2">
                Description (optional)
              </label>
              <textarea
                id="reportDescription"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="input min-h-[100px] resize-y"
                placeholder="Describe the problem you encountered..."
                rows={4}
              />
            </div>
            {error && (
              <div className="text-sm text-theme-error bg-theme-error-bg border border-theme-error-border rounded-lg p-3">
                {error}
              </div>
            )}
            <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="btn btn-primary flex-1"
              >
                {submitting ? 'Submitting...' : 'Submit Report'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}
